import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import MUIDataTable from 'mui-datatables'
import { Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { useHistory } from 'react-router-dom'

import InvoiceFormSidePanel from './InvoiceFormSidePanel'
import InvoiceTotalsInputs from './InvoiceTotalsInputs'
import {
  useFetchBuyersQuery,
  useFetchLineItemsQuery,
  useFetchOrdersQuery,
  useCreateInvoiceMutation,
} from '../api'
import {
  Button,
  Container,
  fileToBase64,
  Form,
  FormikFileField,
  FormikSelect,
  FIELD_REQUIRED,
  InvoiceOrders,
  PageTitle,
  PDFPreview,
  Typography,
} from 'src/modules/ui'
import { formatDateSearch, formatPrice, generateOrderNumber } from 'src/utils'
import moment from 'moment'

const useStyles = makeStyles(theme => ({
  body: {
    backgroundColor: theme.palette.fadedPurple.main,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    [theme.breakpoints.up('desktop')]: {
      flexDirection: 'row',
    },
  },
  content: {
    flexGrow: 1,
  },
  midInput: {
    '& .MuiFormControl-root': {
      minWidth: '100%',
    },
    [theme.breakpoints.up('desktop')]: {
      '& .MuiFormControl-root': {
        minWidth: 160,
      },
    },
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 0,
    },
  },
  next: {
    marginTop: theme.spacing(2),
  },
  table: {
    '& table': { backgroundColor: '#ffffff' },
  },
}))

const mapToOption = ({ id, name }) => ({ value: id, label: name })

const CreateInvoice = () => {
  const buyersQuery = useFetchBuyersQuery(
    {},
    { refetchOnMountOrArgChange: true }
  )
  const [createInvoice] = useCreateInvoiceMutation()
  const history = useHistory()

  if (buyersQuery.isLoading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    )
  }

  const initialValues = {
    buyerId: '',
    creditNote: '0.00',
    creditNoteDocument: null,
    creditNoteDocumentData: '',
    deliveryCharge: '0.00',
    discount: '0.00',
    document: null,
    documentData: '',
    environmentId: '',
    invoiceDate: moment().toDate(),
    invoiceDueDate: moment().add('7', 'days').toDate(),
    invoiceNumber: '',
    orderIds: [],
    otherCharge: '0.00',
    notes: '',
  }

  const handleSubmit = async (
    {
      buyerId,
      creditNote,
      creditNoteDocumentData,
      deliveryCharge,
      discount,
      documentData,
      invoiceDate,
      invoiceDueDate,
      invoiceNumber,
      orderIds,
      otherCharge,
      notes,
    },
    { setErrors }
  ) => {
    try {
      await createInvoice({
        buyerId,
        creditNote,
        creditNoteDocumentData,
        deliveryCharge,
        discount,
        documentData,
        invoiceDate: invoiceDate ? invoiceDate.toISOString() : null,
        invoiceDueDate: invoiceDueDate ? invoiceDueDate.toISOString() : null,
        invoiceNumber,
        orderIds,
        otherCharge,
        notes,
      }).unwrap()
      history.push(`/invoices/list`)
    } catch (err) {
      setErrors(err.data)
    }
  }

  const validationSchema = Yup.object().shape({
    creditNote: Yup.number().typeError('Invalid number').nullable(),
    deliveryCharge: Yup.number().typeError('Invalid number').nullable(),
    discount: Yup.number().typeError('Invalid number').nullable(),
    invoiceNumber: Yup.string().required(FIELD_REQUIRED),
    otherCharge: Yup.number().typeError('Invalid number').nullable(),
    invoiceDate: Yup.date().typeError('Invalid date').required(FIELD_REQUIRED),
    invoiceDueDate: Yup.date().typeError('Invalid date').nullable(),
  })

  return (
    <Container>
      <PageTitle>Create Invoice</PageTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {InnerForm}
      </Formik>
    </Container>
  )
}

const columns = [
  {
    name: 'id',
    label: 'Order Number',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'buyer',
    label: 'Customer',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'price',
    label: 'Total',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'createdAt',
    label: 'Order Date',
    options: {
      filter: false,
      sort: true,
      sortDirection: 'asc',
    },
  },
  {
    name: 'environment',
    label: 'Environment',
    options: {
      filter: false,
      sort: true,
    },
  },
]

const InnerForm = ({ isSubmitting, handleSubmit, setFieldValue, values }) => {
  const [step, setStep] = useState(0)
  const classes = useStyles()
  const buyersQuery = useFetchBuyersQuery()
  const ordersQuery = useFetchOrdersQuery(
    { noInvoice: true },
    {
      refetchOnMountOrArgChange: true,
    }
  )

  const preparePreview = useCallback(async () => {
    try {
      const data = await fileToBase64(values.document)
      setFieldValue('documentData', data)
    } catch (error) {
      console.error(error)
    }
  }, [setFieldValue, values.document])

  useEffect(() => {
    setFieldValue('orderIds', [])
  }, [values.buyerId, setFieldValue])

  useEffect(() => {
    if (values.document) {
      preparePreview()
    } else {
      setFieldValue('documentData', null)
    }
  }, [values.document, preparePreview, setFieldValue])

  if (buyersQuery.isLoading || ordersQuery.isLoading) {
    return <Typography>Loading...</Typography>
  }

  const environmentOptions = values.buyerId
    ? buyersQuery.data.find(({ id }) => id === values.buyerId).environments
    : []

  let orders = []
  if (values.environmentId) {
    orders = ordersQuery.data.filter(
      ({ environmentId }) => environmentId === values.environmentId
    )
  } else if (values.buyerId) {
    orders = ordersQuery.data.filter(
      ({ buyer: { id } }) => id === values.buyerId
    )
  }

  const data = orders.map(order => {
    const { createdAt, buyer, environmentId, price, statusChangedAt } = order
    return {
      ...order,
      id: generateOrderNumber(order),
      serverId: order.id,
      createdAt: formatDateSearch(createdAt),
      statusChangedAt: statusChangedAt && formatDateSearch(statusChangedAt),
      buyer: buyer.name,
      price: formatPrice(price),
      environment: environmentOptions.find(({ id }) => id === environmentId)
        .name,
    }
  })

  const handleSelectionChange = (
    currentRowsSelected,
    allRowsSelected,
    rowsSelected
  ) => {
    setFieldValue(
      'orderIds',
      rowsSelected.map(row => data[row].serverId)
    )
  }

  const selectedIdxs = values.orderIds.map(orderId =>
    data.findIndex(order => order.serverId === orderId)
  )

  const options = {
    download: false,
    elevation: 0,
    print: false,
    responsive: 'simple',
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    selectableRows: 'multiple',
    viewColumns: false,
    onRowSelectionChange: handleSelectionChange,
    rowsSelected: selectedIdxs,
  }

  const next = () => {
    setStep(1)
  }

  return (
    <>
      {step === 0 && (
        <Form className={classes.body}>
          <div className={classes.content}>
            <div className={classes.inputRow}>
              <div className={classes.midInput}>
                <FormikFileField
                  label="Invoice PDF"
                  margin="normal"
                  name="document"
                />
              </div>
            </div>
            <PDFPreview document={values.document} />
            <span className={classes.midInput}>
              <FormikSelect
                label="Buyer"
                margin="normal"
                name="buyerId"
                options={[...buyersQuery.data.map(mapToOption)]}
              />
            </span>
            {values.buyerId && (
              <span className={classes.midInput}>
                <FormikSelect
                  label="Environment"
                  margin="normal"
                  name="environmentId"
                  displayEmpty
                  options={[
                    { value: '', label: 'All Environments' },
                    ...environmentOptions.map(mapToOption),
                  ]}
                  shrink
                />
              </span>
            )}
            {values.buyerId && (
              <div className={classes.table}>
                <MUIDataTable
                  columns={columns}
                  data={data}
                  options={options}
                  customHeadRender={() => <></>}
                  components={{
                    TableToolbar: () => <></>,
                    TableToolbarSelect: () => null,
                  }}
                />
              </div>
            )}
            <div className={classes.next}>
              <Button
                color="primary"
                onClick={next}
                size="large"
                disabled={values.orderIds.length === 0}
              >
                Next
              </Button>
            </div>
          </div>
        </Form>
      )}
      {step === 1 && <CreateInvoiceForm />}
    </>
  )
}

const CreateInvoiceForm = () => {
  const classes = useStyles()
  const [hidePDF, setHidePDF] = useState(false)
  const { handleSubmit, isSubmitting, setFieldValue, values } =
    useFormikContext()
  const lineItemsQuery = useFetchLineItemsQuery(
    { orderId: values.orderIds },
    {
      refetchOnMountOrArgChange: true,
    }
  )
  const ordersQuery = useFetchOrdersQuery({
    refetchOnMountOrArgChange: true,
  })

  const prepareCreditNoteDocumentData = useCallback(async () => {
    const data = await fileToBase64(values.creditNoteDocument)
    setFieldValue('creditNoteDocumentData', data)
  }, [values.creditNoteDocument, setFieldValue])

  useEffect(() => {
    if (values.creditNoteDocument) {
      prepareCreditNoteDocumentData()
    } else {
      setFieldValue('creditNoteDocumentData', null)
    }
  }, [values.creditNoteDocument, prepareCreditNoteDocumentData, setFieldValue])

  if (lineItemsQuery.isLoading || ordersQuery.isLoading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    )
  }

  const selectedOrders = ordersQuery.data.filter(({ id }) =>
    values.orderIds.includes(id)
  )

  const togglePDF = () => {
    setHidePDF(!hidePDF)
  }

  return (
    <>
      <Form className={classes.body}>
        <div className={classes.content}>
          <div className={classes.inputRow}>
            <div className={classes.midInput}>
              <FormikFileField
                label="Invoice PDF"
                margin="normal"
                name="document"
              />
            </div>
            {values.document && (
              <>
                {!hidePDF && (
                  <Button color="secondary" size="large" onClick={togglePDF}>
                    Hide PDF
                  </Button>
                )}
                {hidePDF && (
                  <Button color="secondary" size="large" onClick={togglePDF}>
                    Show PDF
                  </Button>
                )}
              </>
            )}
          </div>
          {!hidePDF && <PDFPreview document={values.document} />}
          <InvoiceOrders
            orders={selectedOrders}
            lineItems={lineItemsQuery.data}
          />
          <InvoiceTotalsInputs orders={selectedOrders} />
        </div>

        <InvoiceFormSidePanel values={values}>
          <Button
            color="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            size="large"
            type="submit"
          >
            Send Invoice
          </Button>
        </InvoiceFormSidePanel>
      </Form>
    </>
  )
}

export default CreateInvoice
